import type { Moment } from "moment-timezone";
import {
	parseAsBoolean,
	parseAsInteger,
	parseAsString,
	parseAsStringLiteral,
	useQueryStates,
} from "nuqs";
import { useEffect } from "react";
import {
	DEFAULT_TEST_SHACK_DATE_PRESET,
	getTestShackPresetRange,
	resolveTestShackUrlRange,
	TEST_SHACK_DATE_FORMAT,
	TEST_SHACK_DATE_PRESET_VALUES,
	TEST_SHACK_MODE_VALUES,
	type TestShackDatePreset,
	type TestShackMode,
} from "../utils/period";

export interface TestShackReportUrlFilters {
	mode: TestShackMode;
	shiftId: number | null;
	startDate: Moment;
	endDate: Moment;
	datePreset: TestShackDatePreset | null;
	sectionId: number | null;
	eventId: number | null;
	heatId: number | null;
	problemsOnly: boolean;
	showMinTargetMax: boolean;
}

export interface TestShackReportUrlFilterUpdate {
	mode?: TestShackMode;
	shiftId?: number | null;
	startDate?: Moment;
	endDate?: Moment;
	datePreset?: TestShackDatePreset | null;
	sectionId?: number | null;
	eventId?: number | null;
	heatId?: number | null;
	problemsOnly?: boolean;
	showMinTargetMax?: boolean;
}

const TEST_SHACK_REPORT_QUERY = {
	mode: parseAsStringLiteral(TEST_SHACK_MODE_VALUES).withDefault("period"),
	shift: parseAsInteger,
	from: parseAsString,
	to: parseAsString,
	preset: parseAsStringLiteral(TEST_SHACK_DATE_PRESET_VALUES).withDefault(
		DEFAULT_TEST_SHACK_DATE_PRESET,
	),
	section: parseAsInteger,
	event: parseAsInteger,
	heat: parseAsInteger,
	problems: parseAsBoolean.withDefault(false),
	specs: parseAsBoolean.withDefault(false),
};

export const useTestShackReportUrlState = () => {
	const [query, setQuery] = useQueryStates(TEST_SHACK_REPORT_QUERY, {
		history: "push",
	});
	const range = resolveTestShackUrlRange(query.from, query.to, query.preset);
	const { start: startDate, end: endDate } = range;

	const filters: TestShackReportUrlFilters = {
		mode: query.mode,
		shiftId: query.shift,
		startDate,
		endDate,
		datePreset: range.datePreset,
		sectionId: query.section,
		eventId: query.event,
		heatId: query.heat,
		problemsOnly: query.problems,
		showMinTargetMax: query.specs,
	};

	useEffect(() => {
		if (range.hasInvalidDate) {
			void setQuery({ from: null, to: null, preset: null });
		}
	}, [range.hasInvalidDate, setQuery]);

	const updateFilters = (updates: TestShackReportUrlFilterUpdate) => {
		const hasExplicitDates = "startDate" in updates || "endDate" in updates;
		const presetRange =
			!hasExplicitDates && updates.datePreset
				? getTestShackPresetRange(updates.datePreset)
				: null;
		const datesChanged =
			"startDate" in updates || "endDate" in updates || "datePreset" in updates;

		return setQuery({
			...("mode" in updates ? { mode: updates.mode } : {}),
			...("shiftId" in updates ? { shift: updates.shiftId } : {}),
			...(datesChanged
				? {
						from: (presetRange?.start ?? updates.startDate ?? startDate).format(
							TEST_SHACK_DATE_FORMAT,
						),
						to: (presetRange?.end ?? updates.endDate ?? endDate).format(
							TEST_SHACK_DATE_FORMAT,
						),
						preset: updates.datePreset ?? null,
					}
				: {}),
			...("sectionId" in updates ? { section: updates.sectionId } : {}),
			...("eventId" in updates ? { event: updates.eventId } : {}),
			...("heatId" in updates ? { heat: updates.heatId } : {}),
			...(updates.problemsOnly !== undefined
				? { problems: updates.problemsOnly }
				: {}),
			...(updates.showMinTargetMax !== undefined
				? { specs: updates.showMinTargetMax }
				: {}),
		});
	};

	return { filters, updateFilters };
};
